import axios, { AxiosRequestConfig } from "axios";
import { v4 } from "uuid";
import { ApiFactory } from "./ApiFactory";
import { localLogger } from "@/utils/localLogger";

export class ApiFactoryAxios extends ApiFactory {
  static get #POOR_CONNEXION_MAX_DISPLAY(): number {
    return 1000 * 60;
  }

  #tm: NodeJS.Timeout | null = null;

  async sendRequest<T>(
    requestOptions: AxiosRequestConfig & { uuid?: string }
  ): Promise<T | void> {
    const fabriqStore = this.fabriqStore;
    const uuid = requestOptions.uuid || v4();
    try {
      if (this.#tm) clearTimeout(this.#tm);
      await localLogger.log(
        `Request [${requestOptions.method}] ${uuid} start: ${requestOptions.url}`
      );
      const startTime = Date.now();
      fabriqStore.startRequest();
      const response = await axios(requestOptions);
      const endTime = Date.now();
      const duration = endTime - startTime;
      fabriqStore.setLastRequestDuration(duration);
      this.#tm = setTimeout(() => {
        fabriqStore.resetRequestDuration();
      }, ApiFactoryAxios.#POOR_CONNEXION_MAX_DISPLAY);
      fabriqStore.endRequest();
      if (response.status < 200 && response.status >= 300) {
        await localLogger.log(`Error request ${uuid} ${response.status}`);
        return Promise.reject(response.data);
      }
      this.resetAttempts();
      await localLogger.log(
        `Request ${uuid} success ${response.status} ${duration}ms`
      );
      return response.data as T;
    } catch (e: any) {
      this.#tm = setTimeout(() => {
        fabriqStore.resetRequestDuration();
      }, ApiFactoryAxios.#POOR_CONNEXION_MAX_DISPLAY);
      fabriqStore.endRequest();
      await localLogger.log(`Error request ${uuid} ${JSON.stringify(e)}`);
      if (e && e.response && e.response.status === 401) {
        await localLogger.log(`Refresh request ${uuid}`);
        return this.refreshToken()
          .then(() => {
            this.resetAttempts();
            return this.sendRequest<T>({
              uuid,
              ...requestOptions,
              headers: this.getHeaders(requestOptions.headers),
            });
          })
          .catch(async (e) => {
            await localLogger.log(`Reconnect request ${uuid}`);
            console.warn(e);
            return this.reconnectAttempt()
              .then(() => {
                this.resetAttempts();
                return this.sendRequest<T>({
                  uuid,
                  ...requestOptions,
                  headers: this.getHeaders(requestOptions.headers),
                });
              })
              .catch(async (e) => {
                await localLogger.log(
                  `Error request reconnect ${uuid} ${JSON.stringify(e)}`
                );
                console.error("Error", e);
                fabriqStore.logout();
              });
          });
      }
    }
  }
}
